import inkableImg from '@/assets/learn-inkable.jpg';
import nonInkableImg from '@/assets/learn-non-inkable.jpg';

export type QuizChoiceQuestion = {
  type: 'choice';
  prompt: string;
  options: string[];
  /** index of the correct option */
  answer: number;
  explain: string;
};

export type QuizImagePickQuestion = {
  type: 'image-pick';
  prompt: string;
  /** Each option is an image + label. The correct one is identified by `answer`. */
  options: { src: string; label: string }[];
  answer: number;
  explain: string;
};

export type QuizQuestion = QuizChoiceQuestion | QuizImagePickQuestion;

export type Quiz = {
  /** matches a LEARN_PATHS slug */
  slug: string;
  title: string;
  blurb: string;
  /** Lesson key written to learn_progress when the user passes */
  lessonKey: string;
  /** 0-1 — pct correct required to "pass" */
  passThreshold: number;
  questions: QuizQuestion[];
};

export const PASS_THRESHOLD = 0.7;

export const LEARN_QUIZZES: Quiz[] = [
  {
    slug: 'rules-basics',
    title: 'Rules Basics Quiz',
    blurb: 'Inking, questing, lore, and challenging.',
    lessonKey: 'quiz-rules-basics',
    passThreshold: PASS_THRESHOLD,
    questions: [
      {
        type: 'choice',
        prompt: 'How much lore do you need to win the game?',
        options: ['10', '15', '20', '25'],
        answer: 2,
        explain: 'First to 20 lore wins.',
      },
      {
        type: 'choice',
        prompt: 'How many cards can you put into your inkwell each turn?',
        options: ['Zero', 'One', 'Two', 'As many as you want'],
        answer: 1,
        explain: 'You may ink at most one card per turn, and only if its border is inkable.',
      },
      {
        type: 'image-pick',
        prompt: 'Tap the card that can be put into the inkwell.',
        options: [
          { src: inkableImg, label: 'Silver swirl border' },
          { src: nonInkableImg, label: 'Plain border' },
        ],
        answer: 0,
        explain: 'The silver swirling border around the card art marks it as inkable.',
      },
      {
        type: 'choice',
        prompt: 'When does a character enter play?',
        options: ['Ready (untapped)', 'Exerted (tapped)', 'In the inkwell', 'In your hand'],
        answer: 1,
        explain: 'Characters enter play exerted — they can\'t quest or challenge the turn they\'re played (unless they have Rush).',
      },
      {
        type: 'choice',
        prompt: 'What does a character do when it quests?',
        options: [
          'Deals damage to an opposing character',
          'Draws you a card',
          'Earns you lore equal to its lore value',
          'Heals all your characters',
        ],
        answer: 2,
        explain: 'Questing exerts the character and gives you lore equal to its lore value (the gold teardrop).',
      },
      {
        type: 'choice',
        prompt: 'A challenge happens when…',
        options: [
          'A ready character of yours targets an exerted enemy character',
          'You play an action card',
          'Your opponent quests',
          'A character is banished',
        ],
        answer: 0,
        explain: 'You exert one of your ready characters to challenge an exerted opposing character; both deal strength damage.',
      },
      {
        type: 'choice',
        prompt: 'A character is banished when its damage equals or exceeds its…',
        options: ['Cost', 'Strength', 'Lore value', 'Willpower'],
        answer: 3,
        explain: 'Willpower is a character\'s health. Damage ≥ willpower → banished.',
      },
    ],
  },
  {
    slug: 'deckbuilding',
    title: 'Deckbuilding Quiz',
    blurb: 'Curves, ink pairs, and win conditions.',
    lessonKey: 'quiz-deckbuilding',
    passThreshold: PASS_THRESHOLD,
    questions: [
      {
        type: 'choice',
        prompt: 'What is the minimum deck size in Lorcana?',
        options: ['40', '50', '60', '100'],
        answer: 2,
        explain: 'Decks must be exactly 60 cards.',
      },
      {
        type: 'choice',
        prompt: 'How many different inks can a single deck contain?',
        options: ['One', 'Up to two', 'Up to three', 'Any number'],
        answer: 1,
        explain: 'A deck can use cards from at most two inks.',
      },
      {
        type: 'choice',
        prompt: 'What is the maximum number of copies of any single card?',
        options: ['2', '3', '4', '6'],
        answer: 2,
        explain: 'Up to 4 copies of any one card per deck.',
      },
      {
        type: 'choice',
        prompt: 'A common rule-of-thumb range for uninkable cards is…',
        options: ['0–3', '4–6', '8–12', '20+'],
        answer: 2,
        explain: 'Most lists run 8–12 uninkable cards to keep ink drops reliable.',
      },
      {
        type: 'choice',
        prompt: 'Which best describes a healthy ink curve?',
        options: [
          'Almost all 1- and 2-drops',
          'Bell curve — many 2–3 cost, some 4–5, a few finishers',
          'Half your deck costs 7+',
          'Even split across every cost',
        ],
        answer: 1,
        explain: 'A healthy curve keeps you playing on tempo each turn.',
      },
      {
        type: 'choice',
        prompt: 'A "win condition" is…',
        options: [
          'Any card that gains lore',
          'The plan/card combo that actually closes the game',
          'A specific Enchanted card',
          'Drawing your last card',
        ],
        answer: 1,
        explain: 'Every deck should have a clear path to 20 lore — that\'s the win con.',
      },
    ],
  },
  {
    slug: 'competitive-play',
    title: 'Competitive Play Quiz',
    blurb: 'Tempo, sideboarding, and reading the meta.',
    lessonKey: 'quiz-competitive-play',
    passThreshold: PASS_THRESHOLD,
    questions: [
      {
        type: 'choice',
        prompt: '"Tempo" in Lorcana usually refers to…',
        options: [
          'Total lore on the board',
          'The pace at which you develop ink and threats relative to your opponent',
          'How fast you shuffle',
          'Average cost of cards in hand',
        ],
        answer: 1,
        explain: 'Tempo is about who is ahead on the resource race each turn.',
      },
      {
        type: 'choice',
        prompt: 'Card advantage means…',
        options: [
          'Having more lore than your opponent',
          'Having more cards/resources available than your opponent',
          'Having more ink in your inkwell',
          'Having Enchanted cards',
        ],
        answer: 1,
        explain: 'Card advantage = more usable resources than the opponent.',
      },
      {
        type: 'choice',
        prompt: 'When should you usually choose to challenge instead of quest?',
        options: [
          'When you have huge lore lead and want to stall',
          'When an opposing character is exerted, hard to remove later, or threatens your win',
          'Always — challenging is always best',
          'Never on turn 1',
        ],
        answer: 1,
        explain: 'Trade when the opponent\'s threat will out-race or out-value you next turn.',
      },
      {
        type: 'choice',
        prompt: 'Sideboarding is most relevant in…',
        options: [
          'A casual single game',
          'Best-of-three matches at events',
          'Online quick play',
          'Drafts',
        ],
        answer: 1,
        explain: 'Sideboards swap cards between games 2/3 of a Bo3 match.',
      },
      {
        type: 'choice',
        prompt: 'Reading the meta means…',
        options: [
          'Memorizing every card',
          'Knowing what archetypes people are playing and tuning for them',
          'Watching streams',
          'Following deckbuilders on Twitter',
        ],
        answer: 1,
        explain: 'Tuning your 60 to beat the popular decks of the moment.',
      },
    ],
  },
  {
    slug: 'trading-collecting',
    title: 'Trading & Collecting Quiz',
    blurb: 'Rarities, pricing, storage, and rotation.',
    lessonKey: 'quiz-trading-collecting',
    passThreshold: PASS_THRESHOLD,
    questions: [
      {
        type: 'choice',
        prompt: 'Which rarity is the rarest pull?',
        options: ['Legendary', 'Super Rare', 'Enchanted', 'Rare'],
        answer: 2,
        explain: 'Enchanted variants are the rarest, special-treatment cards in a set.',
      },
      {
        type: 'choice',
        prompt: 'Card condition has the biggest impact on price when a card is…',
        options: ['Common', 'Uncommon', 'High-value rare/enchanted', 'Promotional'],
        answer: 2,
        explain: 'Condition swings are largest at the top of the price ladder.',
      },
      {
        type: 'choice',
        prompt: 'Sleeves primarily protect your cards from…',
        options: ['Sunlight', 'Surface wear and edge whitening from shuffling', 'Magnets', 'Loud noises'],
        answer: 1,
        explain: 'Sleeves prevent most of the wear that happens during play.',
      },
      {
        type: 'choice',
        prompt: 'A binder is best for…',
        options: [
          'Long-term display of cards you want to keep tradeable',
          'Storing bulk commons',
          'Shipping cards',
          'Stacking ink tokens',
        ],
        answer: 0,
        explain: 'Penny-sleeve + binder is the standard for tradeable singles.',
      },
      {
        type: 'choice',
        prompt: 'Rotation in TCGs refers to…',
        options: [
          'Flipping a card over each turn',
          'Older sets cycling out of the competitive (Standard) format',
          'Switching inks mid-game',
          'A type of shuffle',
        ],
        answer: 1,
        explain: 'Rotation removes older sets from the legal pool to keep the format fresh.',
      },
    ],
  },
];

export function getQuizForPath(slug: string): Quiz | undefined {
  return LEARN_QUIZZES.find((q) => q.slug === slug);
}
